import React, { useContext, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button } from "../ui";

// Icons
const InboxIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
    />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const GroupIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const SearchIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const TicketsManager = () => {
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [groupCount, setGroupCount] = useState(0);
  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
  }, [user.profile]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current?.focus();
      setSearchParam("");
    }
  }, [tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setTab("open");
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  return (
    <div className="relative flex flex-col h-full overflow-hidden bg-white dark:bg-dark-800">
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />

      {/* Main Tabs - Inbox, Closed, Search */}
      <div className="flex-none bg-white dark:bg-dark-800 border-b border-dark-200 dark:border-dark-700">
        <div className="flex">
          <button
            onClick={() => setTab("open")}
            className={clsx(
              "flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors border-b-2",
              tab === "open"
                ? "text-primary-600 dark:text-primary-400 border-primary-500"
                : "text-dark-500 dark:text-dark-400 border-transparent hover:text-dark-700 dark:hover:text-dark-300"
            )}
          >
            <InboxIcon className="w-5 h-5" />
            <span>{i18n.t("tickets.tabs.open.title")}</span>
          </button>
          <button
            onClick={() => setTab("closed")}
            className={clsx(
              "flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors border-b-2",
              tab === "closed"
                ? "text-primary-600 dark:text-primary-400 border-primary-500"
                : "text-dark-500 dark:text-dark-400 border-transparent hover:text-dark-700 dark:hover:text-dark-300"
            )}
          >
            <CheckIcon className="w-5 h-5" />
            <span>{i18n.t("tickets.tabs.closed.title")}</span>
          </button>
          <button
            onClick={() => setTab("search")}
            className={clsx(
              "flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors border-b-2",
              tab === "search"
                ? "text-primary-600 dark:text-primary-400 border-primary-500"
                : "text-dark-500 dark:text-dark-400 border-transparent hover:text-dark-700 dark:hover:text-dark-300"
            )}
          >
            <SearchIcon className="w-5 h-5" />
            <span>{i18n.t("tickets.tabs.search.title")}</span>
          </button>
        </div>
      </div>

      {/* Options Bar */}
      <div className="flex-none flex items-center gap-2 p-3 bg-dark-50 dark:bg-dark-900 border-b border-dark-200 dark:border-dark-700">
        {tab === "search" ? (
          <div className="flex-1 flex items-center gap-2 bg-white dark:bg-dark-800 rounded-full px-4 py-2 border border-dark-200 dark:border-dark-700">
            <SearchIcon className="w-4 h-4 text-dark-400" />
            <input
              ref={searchInputRef}
              type="search"
              placeholder={i18n.t("tickets.search.placeholder")}
              onChange={handleSearch}
              className="flex-1 bg-transparent text-sm text-dark-900 dark:text-white placeholder-dark-400 focus:outline-none"
            />
          </div>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setNewTicketModalOpen(true)}
            >
              {i18n.t("ticketsManager.buttons.newTicket")}
            </Button>
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <label className="flex items-center gap-2 text-xs text-dark-600 dark:text-dark-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllTickets}
                    onChange={() => setShowAllTickets((prev) => !prev)}
                    className="w-4 h-4 rounded border-dark-300 text-primary-500 focus:ring-primary-500"
                  />
                  {i18n.t("tickets.buttons.showAll")}
                </label>
              )}
            />
          </>
        )}
        <TicketsQueueSelect
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />
      </div>

      {/* Open Tab Content - Sub tabs for Attending/Pending */}
      {tab === "open" && (
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Sub Tabs */}
          <div className="flex-none flex border-b border-dark-200 dark:border-dark-700">
            <button
              onClick={() => setTabOpen("open")}
              className={clsx(
                "flex-1 py-2.5 text-sm font-medium transition-colors relative",
                tabOpen === "open"
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-dark-500 dark:text-dark-400 hover:text-dark-700 dark:hover:text-dark-300"
              )}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                {i18n.t("ticketsList.assignedHeader")}
                {openCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-primary-500 text-white rounded-full min-w-[18px]">
                    {openCount}
                  </span>
                )}
              </span>
              {tabOpen === "open" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
              )}
            </button>
            <button
              onClick={() => setTabOpen("pending")}
              className={clsx(
                "flex-1 py-2.5 text-sm font-medium transition-colors relative",
                tabOpen === "pending"
                  ? "text-warning-600 dark:text-warning-400"
                  : "text-dark-500 dark:text-dark-400 hover:text-dark-700 dark:hover:text-dark-300"
              )}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                {i18n.t("ticketsList.pendingHeader")}
                {pendingCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-warning-500 text-white rounded-full min-w-[18px]">
                    {pendingCount}
                  </span>
                )}
              </span>
              {tabOpen === "pending" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-warning-500" />
              )}
            </button>
            <button
              onClick={() => setTabOpen("groups")}
              className={clsx(
                "flex-1 py-2.5 text-sm font-medium transition-colors relative",
                tabOpen === "groups"
                  ? "text-success-600 dark:text-success-400"
                  : "text-dark-500 dark:text-dark-400 hover:text-dark-700 dark:hover:text-dark-300"
              )}
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                <GroupIcon className="w-4 h-4" />
                {i18n.t("ticketsList.groupsHeader")}
                {groupCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-success-500 text-white rounded-full min-w-[18px]">
                    {groupCount}
                  </span>
                )}
              </span>
              {tabOpen === "groups" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-success-500" />
              )}
            </button>
          </div>

          {/* Tickets Lists */}
          <div className="flex-1 overflow-hidden relative">
            <TicketsList
              status="open"
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setOpenCount(val)}
              style={applyPanelStyle("open")}
              isGroup={false}
            />
            <TicketsList
              status="pending"
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setPendingCount(val)}
              style={applyPanelStyle("pending")}
              isGroup={false}
            />
            <TicketsList
              status="open"
              showAll={true}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setGroupCount(val)}
              style={applyPanelStyle("groups")}
              isGroup={true}
            />
          </div>
        </div>
      )}

      {/* Closed Tab Content */}
      {tab === "closed" && (
        <div className="flex-1 overflow-hidden relative">
          <TicketsList
            status="closed"
            showAll={true}
            selectedQueueIds={selectedQueueIds}
          />
        </div>
      )}

      {/* Search Tab Content */}
      {tab === "search" && (
        <div className="flex-1 overflow-hidden relative">
          <TicketsList
            searchParam={searchParam}
            showAll={true}
            selectedQueueIds={selectedQueueIds}
          />
        </div>
      )}
    </div>
  );
};

export default TicketsManager;
